"use client"

import { useMemo } from "react"
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts"
import { IconChartAreaLine } from "@tabler/icons-react"
import { useTranslations } from "next-intl"

import { ApolloPanel } from "@/components/ui/apollo-panel"
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart"
import { Skeleton } from "@/components/ui/skeleton"
import { useCurrency, useFormatCurrency, getCurrencySymbol } from "@/hooks/use-currency"

import type { InvoiceStats } from "@/lib/invoices-api"
import { PRICE_MASK, pricesHidden } from "@/lib/price-visibility"

function abbreviate(value: number, currency: string): string {
  if (pricesHidden()) return PRICE_MASK
  const numValue = Number(value) || 0
  const symbol = getCurrencySymbol(currency)
  if (Math.abs(numValue) >= 1_000_000) return `${symbol}${(numValue / 1_000_000).toFixed(1)}M`
  if (Math.abs(numValue) >= 1_000) return `${symbol}${(numValue / 1_000).toFixed(1)}K`
  return `${symbol}${numValue.toFixed(0)}`
}

/**
 * Render an ISO-ish `month` key (`2026-07` or `2026-07-01`) as a short
 * localized-ish label. Falls back to the raw string when unparseable so a
 * backend that ships "Jul 2026" still renders.
 */
function monthLabel(month: string): string {
  const match = /^(\d{4})-(\d{2})/.exec(month)
  if (!match) return month
  const date = new Date(Number(match[1]), Number(match[2]) - 1, 1)
  if (Number.isNaN(date.getTime())) return month
  return date.toLocaleDateString(undefined, { month: "short", year: "2-digit" })
}

interface RevenueTrendChartProps {
  data: InvoiceStats | null
  isLoading: boolean
  /** Extra classes merged onto the panel. */
  className?: string
}

/**
 * Revenue over time — an area chart over `InvoiceStats.revenue_by_month`, for
 * the accountant / admin dashboards. Same Apollo panel + ChartContainer idiom
 * as the other dashboard charts (200px body, skeleton + empty states, muted
 * axes, gradient fill).
 *
 * PRESENTATIONAL ONLY — the caller owns the `getInvoiceStats()` fetch and the
 * `invoices.view` gate.
 */
export function RevenueTrendChart({ data, isLoading, className }: RevenueTrendChartProps) {
  const t = useTranslations("dashboard")
  const { currency } = useCurrency()
  const { formatCurrencyNoDecimals } = useFormatCurrency()

  const chartData = useMemo(() => {
    const rows = data?.revenue_by_month ?? []
    return rows.map((row) => ({
      month: row.month,
      label: monthLabel(row.month),
      total: Number(row.total) || 0,
    }))
  }, [data])

  const chartConfig: ChartConfig = {
    total: {
      label: t("charts.revenueTrend.seriesLabel"),
      color: "var(--chart-1)",
    },
  }

  const periodTotal = useMemo(
    () => chartData.reduce((sum, row) => sum + row.total, 0),
    [chartData],
  )

  if (isLoading) {
    return (
      <ApolloPanel eyebrow={t("charts.revenueTrend.title")} className={className}>
        <Skeleton className="h-[200px] w-full rounded" />
        <div className="mt-2 flex flex-col items-center gap-1 border-t pt-2">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-6 w-24" />
        </div>
      </ApolloPanel>
    )
  }

  if (!data || chartData.length === 0) {
    return (
      <ApolloPanel eyebrow={t("charts.revenueTrend.title")} className={className}>
        <div className="flex h-[200px] flex-col items-center justify-center text-center">
          <div className="mb-3 rounded-full bg-muted p-3">
            <IconChartAreaLine className="h-6 w-6 text-muted-foreground" />
          </div>
          <p className="text-sm font-medium text-muted-foreground">
            {t("charts.revenueTrend.empty.title")}
          </p>
          <p className="mt-1 text-xs text-muted-foreground/70">
            {t("charts.revenueTrend.empty.body")}
          </p>
        </div>
      </ApolloPanel>
    )
  }

  return (
    <ApolloPanel eyebrow={t("charts.revenueTrend.title")} className={className}>
      <ChartContainer config={chartConfig} className="h-[200px] w-full">
        <AreaChart data={chartData} margin={{ left: 4, right: 8, top: 8, bottom: 0 }}>
          <defs>
            <linearGradient id="revenueTrendFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="var(--chart-1)" stopOpacity={0.7} />
              <stop offset="95%" stopColor="var(--chart-1)" stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <CartesianGrid vertical={false} strokeDasharray="3 3" />
          <XAxis
            dataKey="label"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tick={{ fontSize: 11 }}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            width={56}
            tick={{ fontSize: 11 }}
            tickFormatter={(value: number) => abbreviate(value, currency)}
          />
          <ChartTooltip
            cursor={false}
            content={
              <ChartTooltipContent
                indicator="dot"
                formatter={(value) => (
                  <div className="flex w-full items-center gap-2">
                    <span className="text-muted-foreground">
                      {t("charts.revenueTrend.seriesLabel")}
                    </span>
                    <span className="ml-auto font-medium tabular-nums">
                      {formatCurrencyNoDecimals(Number(value) || 0)}
                    </span>
                  </div>
                )}
              />
            }
          />
          <Area
            dataKey="total"
            type="monotone"
            fill="url(#revenueTrendFill)"
            stroke="var(--chart-1)"
            strokeWidth={2}
          />
        </AreaChart>
      </ChartContainer>
      <div className="mt-2 flex flex-col items-center gap-1 border-t pt-2">
        <span className="text-xs text-muted-foreground">
          {t("charts.revenueTrend.periodTotal")}
        </span>
        <span className="font-serif text-[26px] leading-none tracking-[-0.01em] tabular-nums text-foreground">
          {formatCurrencyNoDecimals(periodTotal)}
        </span>
      </div>
    </ApolloPanel>
  )
}
